/**
 * Sandbox
 *
 * Collapsible code execution container for chat tool results.
 *
 * Primary responsibilities:
 * - Show execution status and title in a compact header
 * - Provide tabbed code, output, and error content slots
 *
 * Dependencies:
 * - Radix Collapsible
 * - Radix Tabs
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { ChevronDown, Code, Loader2 } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export type SandboxState =
  | 'input-streaming'
  | 'input-available'
  | 'output-available'
  | 'output-error'
  | 'pending'
  | 'running'
  | 'completed'
  | 'error'

function getStateLabel(state: SandboxState) {
  switch (state) {
    case 'input-streaming':
    case 'pending':
      return 'Pending'
    case 'running':
      return 'Running'
    case 'output-error':
    case 'error':
      return 'Error'
    case 'input-available':
      return 'Ready'
    case 'output-available':
    case 'completed':
      return 'Completed'
    default:
      return 'Completed'
  }
}

function getStateClass(state: SandboxState) {
  if (state === 'output-error' || state === 'error') {
    return 'border-destructive/40 text-destructive'
  }

  if (state === 'running' || state === 'pending' || state === 'input-streaming') {
    return 'border-primary/40 text-primary'
  }

  return 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
}

export function Sandbox({ className, defaultOpen = true, ...props }: ComponentProps<typeof Collapsible>) {
  return (
    <Collapsible
      className={cn('group/sandbox rounded-md border border-border/70 bg-muted/20', className)}
      defaultOpen={defaultOpen}
      {...props}
    />
  )
}

export interface SandboxHeaderProps extends ComponentProps<typeof CollapsibleTrigger> {
  icon?: ReactNode
  title?: string
  state: SandboxState
}

export function SandboxHeader({
  icon,
  title,
  state,
  className,
  ...props
}: SandboxHeaderProps) {
  const isBusy = state === 'running' || state === 'pending' || state === 'input-streaming'

  return (
    <CollapsibleTrigger
      className={cn(
        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
      {...props}
    >
      <span className="flex min-w-0 items-center gap-2 text-xs font-medium">
        {icon ?? (isBusy ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
        ) : (
          <Code className="size-3.5 shrink-0 text-muted-foreground" />
        ))}
        <span className="truncate">{title ?? 'Code runner'}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Badge variant="outline" className={cn('text-[10px]', getStateClass(state))}>
          {getStateLabel(state)}
        </Badge>
        <ChevronDown className="size-3.5 text-muted-foreground transition-transform group-data-[state=open]/sandbox:rotate-180" />
      </span>
    </CollapsibleTrigger>
  )
}

export function SandboxContent({
  className,
  ...props
}: ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      className={cn(
        'overflow-hidden border-t border-border/60 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
        className
      )}
      {...props}
    />
  )
}

export function SandboxTabs(props: ComponentProps<typeof Tabs>) {
  return <Tabs {...props} />
}

export function SandboxTabsBar({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('max-w-full overflow-x-auto border-b border-border/60 px-2 py-1.5', className)}
      {...props}
    />
  )
}

export function SandboxTabsList({
  className,
  ...props
}: ComponentProps<typeof TabsList>) {
  return <TabsList className={cn('h-8 min-w-max', className)} {...props} />
}

export function SandboxTabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsTrigger>) {
  return <TabsTrigger className={cn('h-6 px-2 text-xs', className)} {...props} />
}

export function SandboxTabContent({
  className,
  ...props
}: ComponentProps<typeof TabsContent>) {
  return <TabsContent className={cn('m-0 p-2', className)} {...props} />
}
